import { OPEN_DIALOG_SELECTOR } from "@src/utils/dialog-selectors";
import { focusMain } from "@src/utils/focus-main";
import { RefObject, useRef } from "react";

const MENU_SELECTOR = '[role="menu"]';
const HEADING_SELECTOR = "h1, h2, h3, h4, h5, h6, [role='heading']";
const OPENER_WATCH_MS = 10_000;

function resolveOpener(active: Element | null): HTMLElement | null {
  if (!(active instanceof HTMLElement) || active === document.body) {
    return null;
  }
  const menu = active.closest(MENU_SELECTOR);
  if (!menu?.id) return active;
  const trigger = document.querySelector<HTMLElement>(
    `[aria-controls="${CSS.escape(menu.id)}"]`,
  );
  return trigger ?? active;
}

function focusRemainingDialog(closing: EventTarget | null): boolean {
  const dialogs = Array.from(
    document.querySelectorAll<HTMLElement>(OPEN_DIALOG_SELECTOR),
  ).filter((dialog) => dialog !== closing);
  const dialog = dialogs[dialogs.length - 1];
  if (!dialog) return false;
  if (dialog.contains(document.activeElement)) return true;
  const heading = Array.from(
    dialog.querySelectorAll<HTMLElement>(HEADING_SELECTOR),
  ).find((element) => element.hasAttribute("tabindex"));
  const target = heading ?? dialog;
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  target.focus();
  return true;
}

function focusFallback(closing: EventTarget | null) {
  if (focusRemainingDialog(closing)) return;
  focusMain();
}

function watchOpenerRemoval(opener: HTMLElement, closing: EventTarget | null) {
  const observer = new MutationObserver(() => {
    if (opener.isConnected) return;
    stopWatching();
    if (document.activeElement === document.body) focusFallback(closing);
  });
  const timer = setTimeout(() => stopWatching(), OPENER_WATCH_MS);
  function stopWatching() {
    observer.disconnect();
    clearTimeout(timer);
    document.removeEventListener("focusin", stopWatching);
  }
  document.addEventListener("focusin", stopWatching);
  observer.observe(document.body, { childList: true, subtree: true });
}

export function useFocusedElementOnOpen(
  open: boolean,
): RefObject<HTMLElement | null> {
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);

  if (open && !wasOpen.current) {
    opener.current = resolveOpener(document.activeElement);
  }
  wasOpen.current = open;

  return opener;
}

export function useRestoreFocusOnClose(
  open: boolean,
  hasTrigger = false,
  returnFocusRef?: RefObject<HTMLElement | null>,
) {
  const opener = useFocusedElementOnOpen(open);

  return (event: Event) => {
    const target = returnFocusRef?.current ?? opener.current;
    if (!target && hasTrigger) return;
    event.preventDefault();
    if (target?.isConnected) {
      target.focus();
      if (document.activeElement === target) {
        watchOpenerRemoval(target, event.target);
        return;
      }
    }
    focusFallback(event.target);
  };
}
